import React, {useState} from 'react';
import {
  Box,
  Text,
  Flex,
  Collapse,
  Menu,
  Image,
  MenuItem,
  MenuDivider,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import {IconChevronUp, IconChevronDown, IconChevronRight} from '@tabler/icons';
import {theme} from '../../../style/theme';
import {useGeolocation} from '../../../hooks/geolocation';
import {getGeneratedImageUrl} from '../../../utils/utils';
import {useUserQuery} from '../../../graphql/generated';
import {BLOG_URL} from '../../../shared/consts';

interface CollectionFooterProps {
  collectionValue: number;
  collectionCurrency: string;
  collectionCurrencyPostfix: string;
  totalPieces: number;
  assetId: string;
  onCertificateAction: (id: string) => void;
  showWalletPass?: boolean;
}

export const CollectionFooter: React.FC<CollectionFooterProps> = ({
  collectionValue,
  collectionCurrency,
  collectionCurrencyPostfix,
  totalPieces,
  assetId,
  onCertificateAction,
  showWalletPass = false,
}) => {
  const userQuery = useUserQuery();
  const {formatPrice} = useGeolocation();
  const [collapse, setCollapse] = useState<boolean>(false);
  const value = formatPrice ? collectionValue?.toFixed(2) : collectionValue;

  return (
    <Flex pos="absolute" bottom="-1px" left={0} flexDir="column" width="100%">
      <Flex
        py="18px"
        px="24px"
        textStyle="h5"
        lineHeight="28px"
        justifyContent="space-between"
        background="calcBtnGrey"
        alignItems="center"
      >
        <Flex alignSelf="flex-end">
          <Text textStyle="h5">{totalPieces}</Text>
          <Text
            alignSelf="end"
            fontSize="12px"
            lineHeight="16.8px"
            fontWeight="600"
            ml="2px"
            mb="2px"
          >
            Pieces
          </Text>
        </Flex>
        <Box textAlign="right">
          <Text fontSize="12px" lineHeight="16.8px" fontWeight="600">
            Ownership
          </Text>
          <Text textAlign="end">
            {`${collectionCurrency}${value}`}
            <Text as="span" fontSize="0.6em">
              {collectionCurrencyPostfix}
            </Text>
          </Text>
        </Box>
      </Flex>
      <Flex
        py={2}
        justifyContent="center"
        alignItems="center"
        flexDir="column"
        background="white"
      >
        <Flex
          alignItems="center"
          py={2}
          cursor="pointer"
          onClick={() => setCollapse(!collapse)}
        >
          {collapse ? (
            <IconChevronDown
              color={theme.colors.aqua['500']}
              size="22px"
              stroke={2}
            />
          ) : (
            <IconChevronUp
              color={theme.colors.aqua['500']}
              size="22px"
              stroke={2}
            />
          )}
          <Text textStyle="h7" fontSize="15px" color="aqua.500">
            {collapse ? 'Hide Actions' : 'View Actions'}
          </Text>
        </Flex>
        <Box width="100%">
          <Collapse in={collapse}>
            <Menu>
              <NextLink href={BLOG_URL} passHref>
                <MenuItem
                  px="24px"
                  display="flex"
                  justifyContent="space-between"
                  textStyle="body2"
                  fontWeight="400"
                  lineHeight="24px"
                >
                  <Text>Buy more Pieces</Text>
                  <IconChevronRight size="16px" stroke={2} />
                </MenuItem>
              </NextLink>
              <MenuDivider stroke="#EDEFF1" opacity="0.3" />

              <MenuItem
                px="24px"
                onClick={() => onCertificateAction(assetId)}
                display="flex"
                justifyContent="space-between"
                textStyle="body2"
                fontWeight="400"
                lineHeight="24px"
              >
                <Text>View my certificate</Text>
                <IconChevronRight size="16px" stroke={2} />
              </MenuItem>
              <MenuDivider stroke="#EDEFF1" opacity="0.3" />

              <NextLink href={BLOG_URL} passHref>
                <MenuItem
                  px="24px"
                  display="flex"
                  justifyContent="space-between"
                  textStyle="body2"
                  fontWeight="400"
                  lineHeight="24px"
                >
                  <Text>Gift Pieces</Text>
                  <IconChevronRight size="16px" stroke={2} />
                </MenuItem>
              </NextLink>
              <MenuDivider stroke="#EDEFF1" opacity="0.3" />

              {showWalletPass && (
                <a
                  target="_blank"
                  href={getGeneratedImageUrl('wallet', {
                    assetId,
                    userId: userQuery.data?.user.id,
                  })}
                  download
                  rel="noreferrer"
                >
                  <MenuItem
                    px="24px"
                    display="flex"
                    justifyContent="space-between"
                    textStyle="body2"
                    fontWeight="400"
                    lineHeight="24px"
                  >
                    <Flex maxW={{base: '25px', md: '250px'}}>
                      <Image
                        src="/assets/add-to-apple-wallet-icon.svg"
                        width="100%"
                        height="100%"
                      />
                    </Flex>

                    <IconChevronRight size="16px" stroke={2} />
                  </MenuItem>
                </a>
              )}
            </Menu>
          </Collapse>
        </Box>
      </Flex>
    </Flex>
  );
};
